import {
  animate,
  AnimationBuilder,
  AnimationFactory,
  AnimationPlayer,
  style,
} from '@angular/animations';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { RTL_LOCALE_CODES } from '@rd-lib/rtl-locales';
import { LocaleFacade } from '@rd/core/facades/locale-facade/locale.facade';
import { IconRegistryService } from '@rd/core/services/icon-registry/icon-registry.service';
import { WindowResizeService } from '@rd/core/services/window-resize/window-resize.service';
import { CoreState } from '@rd/core/store/reducers';
import { IconOptions, Locale } from '@rd/types';
import { Observable, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CarouselInnerItemContainerDirective } from './carousel-inner-item-container.directive';
import { CarouselItemDirective } from './carousel-item.directive';

@Component({
  selector: 'rd-shared-carousel',
  templateUrl: './carousel.component.html',
  styleUrls: ['./carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CarouselComponent implements AfterViewInit, OnDestroy, OnInit {
  @ContentChildren(CarouselItemDirective)
  items: QueryList<CarouselItemDirective>;
  @ViewChildren(CarouselInnerItemContainerDirective, { read: ElementRef })
  innerItemContainers: QueryList<ElementRef>;
  @ViewChild('carousel') carousel: ElementRef;
  @HostBinding('class.rd-shared-carousel') componentClass = true;
  @Input() timing = '400ms cubic-bezier(0.215, 0.61, 0.355, 1)';

  localeSelected$: Observable<Locale>;
  resizeSubscription$: Subscription;
  private destroy$ = new Subject();

  itemsPerNavigate = 2;
  localeDirection: 'ltr' | 'rtl' = 'ltr';
  player: AnimationPlayer;
  navigatedItems = 0; // number of navigated items

  flippableIconOptions: IconOptions = {
    flippable: true,
  };

  constructor(
    private store: Store<CoreState>,
    private builder: AnimationBuilder,
    private cdRef: ChangeDetectorRef,
    private iconRegistryService: IconRegistryService,
    private windowResizeService: WindowResizeService,
    private localeFacade: LocaleFacade,
  ) {}

  ngOnInit(): void {
    this.iconRegistryService.registerIcon('chevron-right');

    this.windowResizeService.windowResizeObservable
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.resizeCarousel();
      });

    this.localeFacade.selectedLocale$
      .pipe(takeUntil(this.destroy$))
      .subscribe((locale) => {
        // @ts-ignore
        const localeDirection = this.isRTLLocale(locale.code) ? 'rtl' : 'ltr';
        this.localeDirection = localeDirection;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  next(): void {
    if (this.nextButtonDisabled()) {
      return;
    }
    this.navigatedItems = this.navigatedItems + this.itemsPerNavigate;
    this.transitionCarousel();
  }

  prev(): void {
    if (this.prevButtonDisabled()) {
      return;
    }
    this.navigatedItems = this.navigatedItems - this.itemsPerNavigate;
    this.transitionCarousel();
  }

  nextButtonDisabled(): boolean {
    return this.navigatedItems + this.itemsPerNavigate >= this.items.length;
  }

  prevButtonDisabled(): boolean {
    return this.navigatedItems <= 0;
  }

  ngAfterViewInit(): void {
    this.resizeCarousel();
  }

  buildAnimation(offset: number): AnimationFactory {
    let transformString: string;

    offset = offset * this.itemsPerNavigate;

    if (this.localeDirection === 'rtl') {
      transformString = `translateX(${offset}px)`;
    } else {
      transformString = `translateX(-${offset}px)`;
    }

    return this.builder.build([
      animate(this.timing, style({ transform: transformString })),
    ]);
  }

  /**
   * Re-sizes the carousel container and triggers `this.transitionCarousel()` to reset the childrens' positions.
   *
   * For use on initial load, and when changing viewport size.
   */
  resizeCarousel(): void {
    // trigger a fresh transition to the current slide to reset the position of the children
    this.transitionCarousel();

    // Must inform Angular of change, so the carousel will re-render upon window resize.
    this.cdRef.detectChanges();
  }

  /**
   * Animates the carousel to the currently selected slide.
   *
   * **You must set `this.currentSlide` before calling this method, or it will have no effect.**
   */
  transitionCarousel(): void {
    // can't transition if there are no items in the first place
    if (
      this.innerItemContainers === undefined ||
      this.innerItemContainers.length === 0
    ) {
      return;
    }

    // assumes that all the item elements will be of the same width
    const itemWidth =
      this.innerItemContainers.first.nativeElement.getBoundingClientRect()
        .width;

    const offset = (this.navigatedItems * itemWidth) / this.itemsPerNavigate;

    const moveAnimation: AnimationFactory = this.buildAnimation(offset);
    this.player = moveAnimation.create(this.carousel.nativeElement);
    this.player.play();
  }

  private isRTLLocale(localeCode: Locale['code']): boolean {
    return RTL_LOCALE_CODES.includes(localeCode);
  }
}
